import React, { useState, useEffect } from 'react';
import {
  Avatar,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Save as SaveIcon,
  Person as PersonIcon,
  PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import { getAvatarSrc } from '../../utils/avatarConstants';
import { useAuth } from '../../context/AuthContext';
import useThemeStyles from '../../theme/useThemeStyles';
import api from '../../utils/api';
import ProfilePhotographyPrivacy from './ProfilePhotographyPrivacy';

const CONTACT_METHODS = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone Call' },
  { value: 'text', label: 'Text Message' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'any', label: 'Any Method' },
];

const TIME_PREFERENCES = [
  { value: 'morning', label: 'Morning (9am–12pm)' },
  { value: 'afternoon', label: 'Afternoon (12pm–5pm)' },
  { value: 'evening', label: 'Evening (5pm–9pm)' },
  { value: 'weekdays', label: 'Weekdays Only' },
  { value: 'weekends', label: 'Weekends Only' },
  { value: 'flexible', label: 'Flexible' },
];

function Profile() {
  const styles = useThemeStyles();
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [savingPersonal, setSavingPersonal] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [error, setError] = useState('');
  const [successPersonal, setSuccessPersonal] = useState('');
  const [successContact, setSuccessContact] = useState('');
  const [personalData, setPersonalData] = useState({
    firstName: '',
    lastName: '',
    preferredName: '',
    email: '',
    profilePicture: '',
    pronouns: '',
    phone: '',
    address: '',
  });
  const [contactData, setContactData] = useState({
    preferredContactMethod: 'email',
    bestTimeToContact: '',
    communicationNotes: '',
  });

  useEffect(() => {
    const loadProfile = async () => {
      if (!user) return;
      try {
        const res = await api.get('/api/users/profile');
        const p = res.data;
        setPersonalData({
          firstName: p.firstName || '',
          lastName: p.lastName || '',
          preferredName: p.preferredName || '',
          email: p.email || '',
          profilePicture: p.profilePicture || '',
          pronouns: p.pronouns || '',
          phone: p.phone || '',
          address: p.address || '',
        });
        setContactData({
          preferredContactMethod: p.preferredContactMethod || 'email',
          bestTimeToContact: p.bestTimeToContact || '',
          communicationNotes: p.communicationNotes || '',
        });
      } catch {
        setPersonalData({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          preferredName: user.preferredName || '',
          email: user.email || '',
          profilePicture: user.profilePicture || '',
          pronouns: user.pronouns || '',
          phone: user.phone || '',
          address: user.address || '',
        });
        setContactData({
          preferredContactMethod: user.preferredContactMethod || 'email',
          bestTimeToContact: user.bestTimeToContact || '',
          communicationNotes: user.communicationNotes || '',
        });
      }
    };
    loadProfile();
  }, [user]);

  const handlePersonalChange = (field) => (event) => {
    setPersonalData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleContactChange = (field) => (event) => {
    setContactData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSavePersonal = async () => {
    try {
      setSavingPersonal(true);
      setError('');
      setSuccessPersonal('');
      const updateData = {
        firstName: personalData.firstName,
        lastName: personalData.lastName,
        preferredName: personalData.preferredName,
        profilePicture: personalData.profilePicture,
        pronouns: personalData.pronouns,
        phone: personalData.phone,
        address: personalData.address,
      };
      const response = await api.put('/api/users/profile', updateData);
      setUser(response.data);
      setSuccessPersonal('Personal information saved successfully!');
      setTimeout(() => setSuccessPersonal(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSavingPersonal(false);
    }
  };

  const handleSaveContact = async () => {
    try {
      setSavingContact(true);
      setError('');
      setSuccessContact('');
      const updateData = {
        preferredContactMethod: contactData.preferredContactMethod,
        bestTimeToContact: contactData.bestTimeToContact,
        communicationNotes: contactData.communicationNotes,
      };
      const response = await api.put('/api/users/profile', updateData);
      setUser(response.data);
      setSuccessContact('Contact preferences saved successfully!');
      setTimeout(() => setSuccessContact(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update contact preferences');
    } finally {
      setSavingContact(false);
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setError('Only PNG and JPEG image files are allowed.');
      event.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    const uploadData = new FormData();
    uploadData.append('avatar', file);

    try {
      setLoading(true);
      setError('');
      setSuccessPersonal('');

      const response = await api.post('/api/users/avatar', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const profilePicture = response.data.profilePicture || response.data.avatarUrl;
      setPersonalData((prev) => ({ ...prev, profilePicture }));
      setUser({ ...user, profilePicture });
      setSuccessPersonal('Avatar updated successfully!');
      setTimeout(() => setSuccessPersonal(''), 3000);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || '';
      setError(msg || 'Failed to upload avatar.');
      event.target.value = '';
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={styles.patterns.loadingState}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container.page}>
      <Typography component="h2" className="pagename" sx={styles.text.pagename}>
        My Profile
      </Typography>
      <Typography variant="body1" sx={styles.text.sectionDescription}>
        Manage your personal information and account settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left: Personal Information (with avatar inside) */}
        <Grid item xs={12} md={6}>
          <Card sx={styles.card.default}>
            <CardContent sx={styles.spacing.card}>
              <Typography variant="h6" sx={styles.text.sectionTitle}>
                Personal Information
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                  <Avatar
                    src={getAvatarSrc(personalData.profilePicture)}
                    sx={{
                      width: 80,
                      height: 80,
                      fontSize: '1.5rem',
                      bgcolor: 'primary.main',
                    }}
                  >
                    {(personalData.firstName?.charAt(0) || personalData.lastName?.charAt(0)) || (
                      <PersonIcon sx={{ fontSize: '1.5rem' }} />
                    )}
                  </Avatar>
                  <input
                    accept="image/png,image/jpeg,image/jpg"
                    style={{ display: 'none' }}
                    id="avatar-upload"
                    type="file"
                    onChange={handleAvatarUpload}
                  />
                  <label htmlFor="avatar-upload">
                    <IconButton
                      component="span"
                      size="small"
                      sx={{
                        position: 'absolute',
                        bottom: -4,
                        right: -4,
                        bgcolor: 'background.paper',
                        border: '2px solid',
                        borderColor: 'divider',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <PhotoCameraIcon fontSize="small" />
                    </IconButton>
                  </label>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {personalData.preferredName || `${personalData.firstName} ${personalData.lastName}`.trim() || 'Your Name'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {personalData.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    PNG or JPEG, max 5MB
                  </Typography>
                </Box>
              </Box>

              <Box sx={styles.patterns.formContainer}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={personalData.firstName}
                  onChange={handlePersonalChange('firstName')}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={personalData.lastName}
                  onChange={handlePersonalChange('lastName')}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Preferred Name"
                  value={personalData.preferredName}
                  onChange={handlePersonalChange('preferredName')}
                  placeholder="e.g. Bob, Sarah"
                  helperText="What should suppliers call you?"
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={personalData.email}
                  disabled
                  helperText="Contact support to change your email address"
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Pronouns"
                  value={personalData.pronouns}
                  onChange={handlePersonalChange('pronouns')}
                  placeholder="e.g. she/her, he/him, they/them"
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={personalData.phone}
                  onChange={handlePersonalChange('phone')}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Address"
                  value={personalData.address}
                  onChange={handlePersonalChange('address')}
                  multiline
                  rows={2}
                  size="small"
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Member since {new Date(user?.createdAt).toLocaleDateString()}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={savingPersonal ? <CircularProgress size={18} /> : <SaveIcon />}
                  onClick={handleSavePersonal}
                  disabled={savingPersonal}
                >
                  {savingPersonal ? 'Saving...' : 'Save'}
                </Button>
              </Box>
              {successPersonal && (
                <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccessPersonal('')}>
                  {successPersonal}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right: Contact Preferences + Photography & Privacy */}
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Card sx={styles.card.default}>
            <CardContent sx={styles.spacing.card}>
              <Typography variant="h6" sx={styles.text.sectionTitle}>
                Contact Preferences
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                How and when suppliers should contact you
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Preferred Contact Method</InputLabel>
                  <Select
                    value={contactData.preferredContactMethod}
                    label="Preferred Contact Method"
                    onChange={handleContactChange('preferredContactMethod')}
                  >
                    {CONTACT_METHODS.map((m) => (
                      <MenuItem key={m.value} value={m.value}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Best Time to Contact</InputLabel>
                  <Select
                    value={contactData.bestTimeToContact}
                    label="Best Time to Contact"
                    onChange={handleContactChange('bestTimeToContact')}
                  >
                    <MenuItem value="">
                      <em>Not specified</em>
                    </MenuItem>
                    {TIME_PREFERENCES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Communication Notes"
                  value={contactData.communicationNotes}
                  onChange={handleContactChange('communicationNotes')}
                  placeholder="e.g. Don't call during work hours, prefer email for initial contact"
                  size="small"
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={savingContact ? <CircularProgress size={18} /> : <SaveIcon />}
                  onClick={handleSaveContact}
                  disabled={savingContact}
                >
                  {savingContact ? 'Saving...' : 'Save'}
                </Button>
              </Box>
              {successContact && (
                <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccessContact('')}>
                  {successContact}
                </Alert>
              )}
            </CardContent>
          </Card>

          <ProfilePhotographyPrivacy />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Profile;
